import { Component } from "@kluntje/core";
// tslint:disable-next-line:max-line-length
import { removeEvent, addClass, forEachNode, findAll, onEvent, removeClass } from "@kluntje/js-utils/lib/dom-helpers";
import { getCurrentMQ } from "Helper/domHelper";
import { LOADED, INITIALIZED, IN_VIEWPORT, PLAYING } from "Helper/cssClasses";
import { removeAllBS } from "Helper/stringHelper";
import { pushIfNew, hasElement } from "Helper/arrayHelper";
import { ViewportObserver } from "@kluntje/services";

import { template } from "./fra-e-lazy-video.template"; // eslint-disable-line

type MQDef = {
  sources: Array<HTMLElement>;
  mqs: number[];
};
class FRALazyVideo extends Component {
  viewportObserver = ViewportObserver.getInstance();
  videoSources?: Map<string, MQDef>;

  constructor() {
    super({
      ui: {
        video: ".fra-js-lazy-video__video",
        playButton: ".fra-e-lazy-video__play-btn:-one",
        pauseButton: ".fra-e-lazy-video__pause-btn:-one",
      },
      events: [
        {
          event: "kl-in-vp",
          target: "this",
          handler: "handleIntersectionChange",
        },
        {
          event: "fra-mq-change",
          target: "window",
          handler: "handleMQChange",
        },
        {
          event: "beforeunload",
          target: "window",
          handler: "unloadVideo",
        },
        {
          event: "click",
          target: "playButton",
          handler: "handlePlayButton",
        },
        {
          event: "click",
          target: "pauseButton",
          handler: "handlePlayButton",
        },
      ],
      initialStates: {
        activeRendition: null,
      },
      reactions: {
        activeRendition: ["loadVideo"],
      },
      preserveChildren: true,
    });
  }

  /**
   * Lifecycle-Hook: Returns Rendering Template
   * @returns {string}
   */
  renderingTemplate(): string {
    return template({ sources: this.sources });
  }

  /**
   * Lifecycle-Hook: Initializes Component after render
   * @override
   */
  afterComponentRender(): void {
    this.viewportObserver.observe(this);
  }

  /**
   * Returns Map containing all Sources strutured
   * by MQs
   * @returns {Map<string, MQDef>}
   */
  get sources(): Map<string, MQDef> {
    // if video-Sources-Map was created before, return it
    if (this.videoSources) {
      return this.videoSources;
    }

    const sourceNodes = findAll(this, "source");
    const sources = new Map();

    forEachNode(
      sourceNodes,
      (sourceNode: HTMLElement) => {
        let currentMq = sourceNode.dataset.dvaMq;
        if (currentMq === undefined) {
          currentMq = "1-5";
        }

        if (sources.has(currentMq)) {
          const mqDef = sources.get(currentMq);
          mqDef.sources.push(sourceNode);
          sources.set(currentMq, mqDef);
        } else {
          sources.set(currentMq, {
            sources: [sourceNode],
            mqs: this.getMQs(currentMq),
          });
        }
      },
      this,
    );

    const fallbackImgs = findAll(this, "img");

    forEachNode(
      fallbackImgs,
      (fallbackImg: HTMLElement) => {
        let currentMq = fallbackImg.dataset.dvaMq;
        if (currentMq === undefined) {
          currentMq = "1-5";
        }

        const fallbackSrc = fallbackImg.dataset.src;

        if (sources.has(currentMq) && fallbackSrc !== undefined) {
          const mqDef = sources.get(currentMq);
          mqDef.fallbackSrc = fallbackSrc;
          sources.set(currentMq, mqDef);
        }
      },
      this,
    );

    this.videoSources = sources;
    return sources;
  }

  /**
   * Returns index of the Video (SourceSet) that should be
   * active on the current mq
   * @returns {number}
   */
  get activeVideo(): number {
    const currentMQ = parseInt(getCurrentMQ().slice(2), 10);
    const foundVideos: number[] = [];
    let idx = 0;

    this.sources.forEach((mqDef: MQDef) => {
      if (hasElement(mqDef.mqs, currentMQ)) {
        foundVideos.push(idx);
      }
      idx++;
    });
    return foundVideos.length ? foundVideos[0] : 0;
  }

  /**
   * Handles Intersection Change, by removing viewportObserver
   * and initializing ImageLoad
   */
  handleIntersectionChange(): void {
    this.viewportObserver.unobserve(this);
    addClass(this, IN_VIEWPORT);
    removeEvent(this, "kl-in-vp", this.handleIntersectionChange, this);
    this.setState({ activeRendition: this.activeVideo });
  }

  /**
   * Handles MQ-Change, by setting activeRendition-State
   */
  handleMQChange(): void {
    this.setState({ activeRendition: this.activeVideo });
  }

  /**
   * Finishes loading, by displaying video and removing loading layer
   *
   * @param {any} e - event info
   */
  handleVideoLoad(e: any): void {
    removeEvent(e.target, "loadeddata", this.handleVideoLoad, this);
    addClass(this, LOADED);
    addClass(this, INITIALIZED);
    addClass(e.target, LOADED);
  }

  /**
   * Handles Video-Loading-Error, by making poster visible
   *
   * @param {any} e - event info
   */
  handleVideoLoadingError(e: any): void {
    removeEvent(e.target, "error", this.handleVideoLoadingError, this);
    const currentVideo = this.ui.video[this.state.activeRendition];

    addClass(this, LOADED);
    addClass(this, INITIALIZED);
    addClass(currentVideo, LOADED);
  }

  /**
   * Loads Active Rendition of Video by loading its sources
   */
  loadVideo(): void {
    if (this.ui.video.length <= this.state.activeRendition) return;

    // unload loaded Rendition and show loading-layer
    this.unloadVideo();

    const currentVideo = this.ui.video[this.state.activeRendition];
    const currentSources = findAll(currentVideo, "source");

    onEvent(currentVideo, "loadeddata", this.handleVideoLoad, this);
    onEvent(currentSources[currentSources.length - 1], "error", this.handleVideoLoadingError, this);

    // add src-Attributes to allow loading
    forEachNode(
      currentSources,
      (source: HTMLElement) => {
        const currentSrc = source.dataset.src;
        if (currentSrc !== undefined) {
          source.setAttribute("src", currentSrc);
        }
      },
      this,
    );

    // load Video
    currentVideo.load();
  }

  /**
   *  Remove all loaded indicating classes to show loading layer
   */
  unloadVideo(): void {
    // remove all loaded indicating classes to show loading layer
    removeClass(this.ui.video, LOADED);
    removeClass(this, LOADED);
    removeClass(this, INITIALIZED);
  }

  /**
   * Event handler, handles click on play or pause button on top of video
   *
   * @param {event} e - MouseEvent
   */
  handlePlayButton(e?: MouseEvent): void {
    if (e) {
      e.preventDefault();
    }
    // get valid video element
    const videoEl = this.ui.video[this.state.activeRendition] as HTMLVideoElement;

    // check whether video plays already, otherwise start video
    if (videoEl.paused || videoEl.ended) {
      this.playVideo();
    } else {
      videoEl.pause();
      removeClass(this, PLAYING);
    }
  }

  /**
   * Try to play video from play button
   */
  async playVideo(): Promise<boolean> {
    const videoEl = this.ui.video[this.state.activeRendition] as HTMLVideoElement;
    try {
      await videoEl.play();
      // activate native video controls
      videoEl.setAttribute("controls", "");
      // attach listener to wait for video to end and remove "playing" state
      onEvent(
        videoEl,
        "ended",
        () => {
          removeClass(this, PLAYING);
        },
        this,
      );
      addClass(this, PLAYING);
      return true;
    } catch (err) {
      // some error occured return to default state
      removeClass(this, PLAYING);
      return false;
    }
  }

  /**
   * Returns Array of MQs for given MQDef
   * @param {string} mqDefs - string describing a Range of Mq (eg "1-3", "1,2,4", "5")
   * @returns {number[]}
   */
  getMQs(mqDefs: string): number[] {
    let mqs: number[] = [];
    removeAllBS(mqDefs)
      .split(",")
      .forEach((mqDef: string) => {
        if (mqDef.length === 1) {
          mqs = pushIfNew(mqs, parseInt(mqDef, 10));
        } else if (mqDef.length > 1) {
          const mqRange = mqDef.split("-");
          const mqRangeStart = parseInt(mqRange[0], 10);
          const mqRangeEnd = parseInt(mqRange[1], 10);
          for (let mq = mqRangeStart; mq <= mqRangeEnd; mq++) {
            mqs = pushIfNew(mqs, mq);
          }
        }
      });
    return mqs;
  }
}

customElements.define("fra-e-lazy-video", FRALazyVideo);
